import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, Star, ThumbsDown, ThumbsUp, Meh, Heart } from 'lucide-react';
import { tableLabel } from '../lib/tableLabel';

const ratingConfig = {
  bad: { icon: ThumbsDown, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Bad' },
  okay: { icon: Meh, color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'Okay' },
  good: { icon: ThumbsUp, color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: 'Good' },
  excellent: { icon: Heart, color: 'text-gold', bg: 'bg-gold/20', label: 'Excellent' },
};

export default function FeedbackTab() {
  const [feedback, setFeedback] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [fbRes, alertRes] = await Promise.all([api.get('/feedback'), api.get('/feedback/alerts')]);
      setFeedback(fbRes.data);
      setAlerts(alertRes.data);
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = feedback.reduce((acc, f) => { acc[f.rating] = (acc[f.rating] || 0) + 1; return acc; }, {});
  const total = feedback.length;

  return (
    <div>
      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-red-400 font-sans text-xs uppercase tracking-wider font-bold" data-testid="feedback-alerts-count">{alerts.length} Negative Feedback Alerts</span>
          </div>
          {alerts.slice(0, 3).map(a => (
            <div key={a.id} className="flex items-center justify-between text-sm py-1">
              <span className="text-zinc-300 font-sans">{tableLabel(a.table_number)} - {a.customer_name}</span>
              <div className="flex items-center gap-2">
                <Badge className={`${ratingConfig[a.rating]?.bg} ${ratingConfig[a.rating]?.color} border-0 font-sans text-xs`}>{a.rating}</Badge>
                {a.comment && <span className="text-zinc-500 text-xs font-sans truncate max-w-[200px]">"{a.comment}"</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {Object.entries(ratingConfig).map(([key, config]) => {
          const Icon = config.icon;
          const count = stats[key] || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={key} className="bg-white/[0.03] border border-white/5 rounded-sm p-3 text-center">
              <Icon className={`w-5 h-5 mx-auto mb-1 ${config.color}`} />
              <p className="text-white font-mono text-lg font-bold">{count}</p>
              <p className="text-zinc-500 text-xs font-sans">{config.label} ({pct}%)</p>
            </div>
          );
        })}
      </div>

      {/* Feedback List */}
      <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-3">Recent Feedback ({total})</h3>
      <div className="space-y-2">
        {feedback.map(f => {
          const config = ratingConfig[f.rating] || ratingConfig.okay;
          const Icon = config.icon;
          return (
            <div key={f.id} className="flex items-start gap-3 p-3 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`feedback-${f.id}`}>
              <div className={`w-8 h-8 rounded-sm ${config.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${config.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-sans text-sm">{f.customer_name}</span>
                  <span className="text-zinc-600 text-xs font-sans">{tableLabel(f.table_number)}</span>
                  <Badge className={`${config.bg} ${config.color} border-0 font-sans text-xs`}>{f.rating}</Badge>
                </div>
                {f.comment && <p className="text-zinc-400 text-xs font-sans mt-1">{f.comment}</p>}
                <p className="text-zinc-600 text-xs font-sans mt-1">{new Date(f.created_at).toLocaleString()}</p>
              </div>
            </div>
          );
        })}
        {feedback.length === 0 && <p className="text-zinc-500 font-sans text-center py-8" data-testid="no-feedback">No feedback yet</p>}
      </div>
    </div>
  );
}
